import React, { MouseEvent, MutableRefObject, useCallback, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import { FormikProps } from 'formik';
import * as Sentry from '@sentry/react';
import { Hidden } from '@material-ui/core';
import ArchiveOutlinedIcon from '@material-ui/icons/ArchiveOutlined';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';
import SendOutlinedIcon from '@material-ui/icons/SendOutlined';
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined';
import UnarchiveOutlinedIcon from '@material-ui/icons/UnarchiveOutlined';
import { SpeechBubbleIcon } from '@castiron/components';
import { backendStateToFrontendState, FrontendTransactionState, Transaction } from '@castiron/domain';
import { useTracking, defaultTimeZone } from '@castiron/utils';
import { transactionRepository } from '../../domain';
import { getService } from '../../firebase';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { openModal } from '../../store/reducers/modalConductor';
import { listCustomTransactionsAction, listTransactionsAction } from '../../store/reducers/transactions';
import Dropdown, { DropDownOption } from '../Dropdown';
import EllipsisMenu, { EllipsisMenuOption } from '../Menus/EllipsisMenu';
import Spinner from '../Spinner';
import { QuoteValues } from './EditQuote';
import { availableQuoteActions, prepareQuoteSegmentData, validateQuote } from './QuoteUtils';

type Props = {
  editing: boolean;
  transaction: Transaction;
  context?: 'dashboard' | 'edit';
  formikRef?: MutableRefObject<FormikProps<QuoteValues>>;
  hasSubTransactions?: boolean;
  handleRefresh?: () => void;
};

const sendQuoteUpdatedService = getService('orders', 'sendquoteupdatedemail');
const printOrderService = getService('orders', 'printorder');

const QuoteActionsDropdown: React.FC<Props> = (props: Props) => {
  const { transaction, editing, formikRef, hasSubTransactions, context, handleRefresh } = props;

  const history = useHistory();
  const dispatch = useAppDispatch();
  const { trackEvent } = useTracking();

  const { shop } = useAppSelector(state => ({
    shop: state.shops.shop,
  }));

  const tz = shop.config?.timeZone || defaultTimeZone;

  const [showSpinner, setShowSpinner] = useState(false);
  const [subTransactions, setSubTransactions] = useState([]);

  const getSubTransactions = async id => {
    const subs = await Promise.resolve(transactionRepository.getSubTransactions(id));

    setSubTransactions(subs);
  };

  useEffect(() => {
    //only doing this when we know the subTransactions haven't been fetched previously to reduce unnecessary calls
    if (!!transaction && hasSubTransactions == undefined) {
      getSubTransactions(transaction.id);
    }
  }, [transaction, hasSubTransactions]);

  const trackAction = (action: string): void => {
    trackEvent('Quote Action Clicked', {
      action,
      url: window.location.href,
      ...prepareQuoteSegmentData(transaction),
    });
  };

  const existingStatus = backendStateToFrontendState(transaction, 'quote');
  const trackStatusChange = (newStatus: FrontendTransactionState): void => {
    trackEvent('Quote Status Changed', {
      url: window.location.href,
      previousStatus: existingStatus,
      newStatus,
      ...prepareQuoteSegmentData(transaction),
    });
  };

  const actions = availableQuoteActions(transaction).filter(
    a => !(a == 'markAsPaid' && (hasSubTransactions || !_.isEmpty(subTransactions))),
  );

  const formikForm = formikRef?.current;

  const sendQuote = async (event: MouseEvent<HTMLButtonElement>) => {
    const sendQuoteTx = Sentry.startTransaction({
      op: 'submit',
      name: 'Send Quote',
    });
    Sentry.getCurrentHub().configureScope(scope => {
      scope.setSpan(sendQuoteTx);
      scope.setUser({
        id: shop.id,
        email: shop.email,
        username: shop.websiteUrl,
      });
    });

    if (editing && formikForm) {
      const span = sendQuoteTx.startChild({
        op: 'submit',
        description: 'Submit Form',
      });
      await formikForm.submitForm();
      span.finish();
    }

    /* I hate that I'm doing this but there is no guarantee the transaction in the props is up to date enough for our purposes here
     * get the most recent from the source of truth after the form is submitted
     */
    const upToDateTransaction = await transactionRepository.get(transaction.id);

    trackAction('sendQuote');
    if (validateQuote(upToDateTransaction).length > 0) {
      history.push(`/quotes/edit/${upToDateTransaction.id}?error=missingRequired`);
      window.scrollTo(0, 0);

      return;
    }
    const subtotal = upToDateTransaction.totals?.subtotal || 0;
    if (subtotal < 50) {
      history.push(`/quotes/edit/${upToDateTransaction.id}?error=illegalTotal`);
      window.scrollTo(0, 0);

      return;
    }

    dispatch(
      openModal({
        modalType: 'QUOTE_SEND_MODAL',
        modalProps: {
          show: true,
          transaction: upToDateTransaction,
        },
      }),
    );
  };

  const resendQuote = async (event: MouseEvent<HTMLButtonElement>) => {
    trackAction('resendQuote');

    if (editing && formikForm) {
      /* this should do everything we do in the else below for us */
      await formikForm.submitForm();
    } else {
      setShowSpinner(true);
      if (validateQuote(transaction).length > 0) {
        history.push(`/quotes/edit/${transaction.id}?error=missingRequired`);
        window.scrollTo(0, 0);
        return;
      }
      const subtotal = transaction.totals?.subtotal || 0;
      if (subtotal < 0.5) {
        history.push(`/quotes/edit/${transaction.id}?error=illegalTotal`);
        window.scrollTo(0, 0);
        return;
      }

      try {
        await sendQuoteUpdatedService({ transactionId: transaction.id });
        setShowSpinner(false);
      } catch (error) {
        setShowSpinner(false);
      }
    }
  };

  const openContactModal = (event: MouseEvent<HTMLButtonElement>): void => {
    trackAction('sendMessage');
    dispatch(
      openModal({
        modalType: 'CONTACT_MODAL',
        modalProps: {
          show: true,
          email: transaction.customerObj?.email,
          customerId: transaction.customerObj?.id,
        },
      }),
    );
  };

  const cancel = async (event: MouseEvent<HTMLButtonElement>) => {
    trackAction('cancel');
    dispatch(
      openModal({
        modalType: 'REJECT_CUSTOM_ORDER',
        modalProps: {
          show: true,
          transaction,
        },
      }),
    );
  };

  const setArchived = (isArchived: boolean) => async (event: MouseEvent<HTMLButtonElement>) => {
    setShowSpinner(true);

    trackAction(isArchived ? 'archive' : 'unarchive');
    const transactionUpdateP = transactionRepository.updateProps(transaction.id, {
      isArchived: isArchived,
    });

    let subTransactionUpdateP;
    //get subTransactions if we know they're there and haven't gotten them locally yet--reduce unnecessary calls in initial useEffect
    const subs = !_.isEmpty(subTransactions)
      ? subTransactions
      : hasSubTransactions
      ? await Promise.resolve(transaction?.getSubTransactions())
      : [];
    if (!_.isEmpty(subs)) {
      subTransactionUpdateP = transactionRepository.updateProps(subs[0].id, {
        isArchived: isArchived,
      });
    }

    await Promise.all([transactionUpdateP, subTransactionUpdateP]);

    /* hack a new transaction here to confirm new status */
    trackStatusChange(backendStateToFrontendState({ ...transaction, isArchived }, 'quote'));
    dispatch(listTransactionsAction(shop.id));
    dispatch(listCustomTransactionsAction(shop.id));

    setShowSpinner(false);
    if (context === 'dashboard' && handleRefresh) {
      handleRefresh();
    } else if (isArchived) {
      history.push('/quotes');
    }
  };

  const viewQuote = (event: MouseEvent<HTMLButtonElement>) => {
    trackAction('view');
    history.push(`/orders/edit/${transaction.id}`);
  };

  const markAsPaid = async event => {
    if (formikForm) {
      await formikForm.setFieldValue('markedAsPaid', true);
      await formikForm.submitForm();
    }

    const newTx = await transactionRepository.get(transaction.id);
    dispatch(
      openModal({
        modalType: 'MARK_AS_PAID_MODAL',
        modalProps: {
          show: true,
          transaction: newTx,
        },
      }),
    );
  };

  const sendPayReminder = async (event: MouseEvent<HTMLButtonElement>) => {
    trackAction('sendPayReminder');
    dispatch(
      openModal({
        modalType: 'SEND_PAYMENT_REMINDER_MODAL',
        modalProps: {
          transactionId: transaction?.id,
        },
      }),
    );
  };

  const handlePrint = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const printFrame = document.createElement('iframe');
    printFrame.style.display = 'none';
    document.title = `Invoice ${transaction?.order?.orderNumber || ''}`;

    const response = await printOrderService({
      transactionId: transaction.id,
      timeZone: tz,
      title: `Invoice ${transaction?.order?.orderNumber || ''}`,
      orderContext: 'quote',
    });

    printFrame.srcdoc = response.html;

    document.body.appendChild(printFrame);

    printFrame.onload = () => {
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      });
    };

    trackEvent('Print Invoice', { invoicesPrinted: 1 });
  };

  const actionMapping = {
    markAsPaid: {
      label: 'Mark as Paid',
      icon: <CheckCircleOutlineOutlinedIcon />,
      onClick: markAsPaid,
    },
    sendQuote: {
      label: `${editing ? 'Save and ' : ''}Send Invoice`,
      icon: <SendOutlinedIcon />,
      onClick: sendQuote,
    },
    resendQuote: {
      label: `${editing ? 'Save and ' : ''}Re-send Invoice`,
      icon: <SendOutlinedIcon />,
      onClick: resendQuote,
    },
    sendPayReminder: {
      label: 'Send Pay Reminder',
      icon: <SendOutlinedIcon />,
      onClick: sendPayReminder,
    },
    print: {
      label: 'Print',
      icon: <PrintOutlinedIcon />,
      onClick: handlePrint,
    },
    sendMessage: {
      label: 'Message Customer',
      icon: <SpeechBubbleIcon />,
      onClick: openContactModal,
    },
    view: {
      label: 'View Order',
      icon: <ShoppingCartOutlinedIcon />,
      onClick: viewQuote,
    },
    archive: {
      label: 'Archive Invoice',
      icon: <ArchiveOutlinedIcon />,
      onClick: setArchived(true),
    },
    unarchive: {
      label: 'Unarchive Invoice',
      icon: <UnarchiveOutlinedIcon />,
      onClick: setArchived(false),
    },
    cancel: {
      label: 'Cancel Invoice',
      icon: <CancelOutlinedIcon />,
      color: 'error',
      onClick: cancel,
    },
  };

  /*
   * Filtering out undefineds for now, will take a closer look next time I'm here
   */
  const actionMenuOptions = actions.map(action => actionMapping[action] as DropDownOption).filter(a => !!a);
  const actionMenuEllipsisOptions = actions.map(action => actionMapping[action] as EllipsisMenuOption).filter(a => !!a);

  return (
    <>
      <Spinner show={showSpinner} size="fullscreen" />
      {context === 'dashboard' ? (
        <EllipsisMenu options={actionMenuEllipsisOptions} />
      ) : (
        <>
          <Hidden smDown>
            <Dropdown title="Actions" options={actionMenuOptions} />
          </Hidden>
          <Hidden mdUp>
            <EllipsisMenu options={actionMenuEllipsisOptions} />
          </Hidden>
        </>
      )}
    </>
  );
};

export default QuoteActionsDropdown;
